<i18n src="./resources/locales.json"></i18n>

<template>
  <v-dialog :value="dialog" persistent max-width="500px">
    <v-card color="surface lighten-1">
      <div class="card-header">
        <v-card-title class="xtium-title mb-1">
          {{ $t('title') }}
        </v-card-title>
        <v-card-subtitle class="xtium-subtitle">
          {{ $t('currentEndpointDestinationLabel') }}
          <span class="green--text text--lighten-3">
            {{ endpointCurrentDestination | formatDestination(endpointCurrentType) }}
          </span>
        </v-card-subtitle>
      </div>

      <v-divider />

      <v-card-text class="card-content">
        <v-card-text v-if="message" class="warning message">
          <v-icon small>mdi-alert</v-icon>
          {{ message }}
        </v-card-text>

        <v-form v-model="isValidEndpoint">
          <v-radio-group v-model="endpointType">
            <v-radio
              v-for="terminal in terminalOptions"
              :key="terminal.id"
              :label="terminal.label"
              :value="terminal.value"
            />
          </v-radio-group>

          <v-select
            v-if="isPrivate"
            v-model="endpointPrivatePhoneDestination"
            :items="privatePhones"
            item-text="uri"
            item-value="uri"
            :label="$t('privatePhoneDestinationSelectorLabel')"
            :rules="endpointPrivatePhoneDestinationRules"
          >
          </v-select>

          <v-text-field v-if="isHPBX" :value="endpointDefaultDestinationText" readonly></v-text-field>

          <v-text-field v-if="isInternal" :value="endpointDestination" readonly></v-text-field>

          <v-text-field
            v-if="isCustom"
            v-model="endpointCustomDestination"
            :label="$t('customEndpointDestinationInputLabel')"
            :rules="endpointCustomDestinationRules"
          >
          </v-text-field>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click.stop="cancel">
          <span v-once>{{ $t('actions.cancel') }}</span>
        </v-btn>
        <v-btn color="primary darken-1" :disabled="isSaveActionDisabled" @click="save">
          <span v-once>{{ $t('actions.save') }}</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { TERMINAL_TYPES } from '@/constants/terminalTypes';
import EcsSoftPhoneMixin from '@/components/ecs-soft-phone/EcsSoftPhoneMixin';

export default {
  name: 'EcsEndpointSelectionDialogPresentation',

  filters: {
    formatDestination(value, format) {
      return format === TERMINAL_TYPES.HPBX_PHONE ? `HPBX (${value.split('@')[0]})` : value;
    }
  },

  mixins: [EcsSoftPhoneMixin],

  props: {
    endpointCurrentType: {
      type: String,
      default() {
        return null;
      }
    },

    endpointCurrentDestination: {
      type: String,
      default() {
        return null;
      }
    },

    endpointDefaultDestination: {
      type: String,
      default() {
        return null;
      }
    },

    privatePhones: {
      type: Array,
      default() {
        return [];
      }
    }
  },

  data() {
    return {
      dialog: false,
      message: null,
      endpointType: this.endpointCurrentType,
      endpointPrivatePhoneDestination:
        this.endpointCurrentType === TERMINAL_TYPES.PRIVATE_PHONE ? this.endpointCurrentDestination : null,
      endpointCustomDestination:
        this.endpointCurrentType === TERMINAL_TYPES.CUSTOM ? this.endpointCurrentDestination : null,
      endpointPrivatePhoneDestinationRules: [value => !!value || this.$t('dataRequired')],
      endpointCustomDestinationRules: [value => !!value || this.$t('dataRequired')],
      isValidEndpoint: false
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent', 'telephoneTerminals']),

    ...mapGetters('EVENT_TIMING', ['isWaitingForAgentTerminal']),

    externalHPBX() {
      return this.telephoneTerminals.find(terminal => terminal.terminalType === TERMINAL_TYPES.HPBX_PHONE);
    },

    privatePhone() {
      return this.telephoneTerminals.find(terminal => terminal.terminalType === TERMINAL_TYPES.PRIVATE_PHONE);
    },

    internalPhone() {
      return this.telephoneTerminals.find(terminal => terminal.terminalType === TERMINAL_TYPES.INTERNAL_PHONE);
    },

    terminalOptions() {
      const customPhone = { terminalType: TERMINAL_TYPES.CUSTOM };
      const sortedOptions = [this.externalHPBX, this.hasPrivatePhones && this.privatePhone, customPhone].filter(
        terminal => terminal
      );

      if (this.enableWebRTCSoftPhone) {
        sortedOptions.splice(1, 0, this.internalPhone);
      }

      if (sortedOptions.length <= 1) {
        this.openEndpointSelection();
      }

      return sortedOptions
        .filter(terminal => terminal)
        .map(terminal => {
          return {
            id: terminal.terminalType,
            label:
              this.$t('terminalTypes.' + terminal.terminalType) +
              (terminal.isDefault ? ` (${this.$t('default')})` : ''),
            value: terminal.terminalType
          };
        });
    },

    hasChanges() {
      return (
        this.endpointCurrentType !== this.endpointType || this.endpointCurrentDestination !== this.endpointDestination
      );
    },

    hasPrivatePhones() {
      return this.privatePhones.length > 0;
    },

    endpointDestination() {
      let destination = null;

      switch (this.endpointType) {
        case 'HPBXPhone':
          destination = this.externalHPBX && this.externalHPBX.uri;
          break;
        case 'PrivatePhone':
          destination = this.endpointPrivatePhoneDestination;
          break;
        case 'Custom':
          destination = this.endpointCustomDestination;
          break;
        case 'InternalPhone':
          destination = this.internalPhone.userName || this.internalPhone.uri;
          break;
        default:
          break;
      }

      return destination;
    },

    endpointDefaultDestinationText() {
      return this.endpointDefaultDestination;
    },

    isHPBX() {
      return this.endpointType === TERMINAL_TYPES.HPBX_PHONE;
    },

    isPrivate() {
      return this.endpointType === TERMINAL_TYPES.PRIVATE_PHONE;
    },

    isCustom() {
      return this.endpointType === TERMINAL_TYPES.CUSTOM;
    },

    isInternal() {
      return this.endpointType === TERMINAL_TYPES.INTERNAL_PHONE;
    },

    isSaveActionDisabled() {
      return !this.hasChanges || !this.isValidEndpoint;
    }
  },

  watch: {
    endpointType(newValue) {
      if (newValue === TERMINAL_TYPES.PRIVATE_PHONE) {
        this.setDefaultPrivatePhoneDestination();
      }
    }
  },

  created() {},

  methods: {
    ...mapActions('SETTINGS', ['updateUseSoftPhone']),

    ...mapActions('EVENT_TIMING', ['addEventTimingConstraintForAgentTerminal']),

    cancel() {
      this.message = null;
      this.dialog = false;
    },

    open(event) {
      // restore initial type before open the dialog
      // if state was changed before leave
      if (this.hasChanges) {
        this.endpointType = this.endpointCurrentType;
      }

      this.message = event && event.message;
      this.dialog = true;
    },

    openEndpointSelection() {
      setTimeout(() => {
        this.$root.$emit('ecs-endpoint-selection-dialog-open', { message: this.$t('noTerminalOptions') });
      }, 3000);
    },

    updateAgentEndPoint() {
      this.updateSoftPhoneRegistrationErrors(false);
      this.$emit('update-agent-endpoint', {
        type: this.endpointType,
        destination: this.endpointDestination
      });
    },

    save() {
      if (!this.isSaveActionDisabled) {
        this.updateAgentEndPoint();
      }

      this.message = null;
      this.dialog = false;
    },

    setDefaultPrivatePhoneDestination() {
      const defaultPrivatePhone = this.privatePhones.find(elem => elem.isDefault);
      if (defaultPrivatePhone) {
        this.endpointPrivatePhoneDestination = defaultPrivatePhone.uri;
      }
    }
  }
};
</script>

<style scoped>
.card-header {
  height: 80px;
}

.card-content {
  padding: 12px 24px !important;
  background: var(--v-surface-base);
}

.warning.message {
  padding: 12px;
  border-radius: 4px;
  color: #00000099;
  line-height: 20px;

  .mdi-alert {
    color: #00000099;
    margin: -2px 4px 0 0;
  }
}
</style>
